import type { Tone } from '../theme/tokens';

/** Display metadata of an order-api `orderStatusId`; one place for labels and chip tones. */
export interface OrderStatusMeta {
    readonly id: number;
    readonly label: string;
    readonly tone: Tone;
}

export const ORDER_STATUSES: readonly OrderStatusMeta[] = Object.freeze([
    { id: 1, label: 'Created', tone: 'info' },
    { id: 2, label: 'Production', tone: 'warning' },
    { id: 3, label: 'Shipped', tone: 'success' },
    { id: 4, label: 'Delivered', tone: 'success' },
    { id: 5, label: 'Canceled', tone: 'neutral' },
    { id: 6, label: 'Error', tone: 'danger' },
    { id: 7, label: 'Partially Shipped', tone: 'info' },
    { id: 8, label: 'Hold', tone: 'brand' },
    { id: 10, label: 'Printed', tone: 'warning' },
    { id: 11, label: 'Packaged', tone: 'warning' },
    { id: 12, label: 'Declined', tone: 'neutral' },
    { id: 13, label: 'Picked', tone: 'warning' },
] as OrderStatusMeta[]);

const BY_ID: Readonly<Record<number, OrderStatusMeta>> = ORDER_STATUSES.reduce((acc, s) => ({ ...acc, [s.id]: s }), {});

const UNKNOWN: OrderStatusMeta = { id: -1, label: 'Unknown', tone: 'neutral' };

/** Metadata for a status id; ids the frontend does not know get a neutral "Status N" chip instead of crashing. */
export const orderStatus = (id: number | string | null | undefined): OrderStatusMeta => {
    if (id == null || id === '') return UNKNOWN;
    const numeric = Number(id);
    if (!Number.isFinite(numeric)) return UNKNOWN;
    return BY_ID[numeric] ?? { id: numeric, label: `Status ${numeric}`, tone: 'neutral' };
};

export interface ItemStatusMeta {
    readonly label: string;
    readonly tone: Tone;
}

/** `itemStatusId` of an order item (extra-features clients only). */
export const ITEM_STATUSES: Readonly<Record<number, ItemStatusMeta>> = Object.freeze({
    1: { label: 'New', tone: 'info' },
    2: { label: 'Shipped', tone: 'success' },
    3: { label: 'Canceled', tone: 'danger' },
    4: { label: 'Declined', tone: 'neutral' },
});

export const itemStatus = (id: number | string | null | undefined, fallback?: string): ItemStatusMeta | null => {
    const numeric = Number(id);
    if (Number.isFinite(numeric) && ITEM_STATUSES[numeric]) return ITEM_STATUSES[numeric];
    return fallback ? { label: fallback, tone: 'neutral' } : null;
};
